import React, { useState, useEffect } from "react";
import Dialog from "../../components/Dialog";
import Button from "../../components/Button";
import { roundFloat } from "../../utils/round-float";
import Slider from "rc-slider";
import ThousandsSeparator from "../../utils/thousands-separator";
import { Badge, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import Decimal from "decimal.js-light";
import { motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import Countdown from "react-countdown";
import { Assets } from "../../constants/assets";
import BuildingDetailTab from "./BuildingDetailTab";
import { FTPURL } from "../../utils/Network";
import { useNavigate, useLocation } from "react-router-dom";
import { PlayerBuildingState } from "../../types/buildings";
import { BuildCostPreview } from "./BuildCost";
import { RepairPromotionEnd } from "../../constants/events";
import { useAppTranslation } from "../../i18n/useAppTranslation";

interface BuildingDetailDialogProps {
  open: boolean;
  onClose: () => void;
  building: any;
  onUpgrade: (id: string | number) => void;
  onRepair: (
    id: string | number,
    options?: any
  ) => Promise<boolean | void> | void;
  onCollect: (id: string | number) => void;
  onBuild: (id: string | number, name: string, item?: any) => void;
  loading: boolean;
  detailLoading: boolean;
  refreshData: () => void;
  refreshUserInfo: () => void;
  // New props for hash-based opening
  storeBuildings?: any[];
  staticBuildings?: any[];
  getBuilding?: (id: number | string, item?: any) => void;
}

const BuildingDetailDialog: React.FC<BuildingDetailDialogProps> = ({
  open,
  onClose,
  building,
  onUpgrade,
  onRepair,
  onCollect,
  onBuild,
  loading,
  detailLoading,
  refreshData,
  refreshUserInfo,
  storeBuildings,
  staticBuildings,
  getBuilding,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppTranslation(["common", "village"]);
  const [activeTab, setActiveTab] = useState(0);
  const [clegCount, setClegCount] = useState(0);
  const [stoneCount, setStoneCount] = useState(0);
  const [ironCount, setIronCount] = useState(0);
  const [maxSupply, setMaxSupply] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    submit: null,
    title: "",
    msg: "",
  });
  // Initialize resource counts when building changes
  useEffect(() => {
    if (building && building.buildingId !== 6) {
      let totalAmount =
        (building?.repairCost || 0) +
        (building?.repairIronCost || 0) * (building?.ironMarketPrice || 0) +
        (building?.repairStoneCost || 0) * (building?.stoneMarketPrice || 0);
      totalAmount = +roundFloat(totalAmount);
      setMaxSupply(totalAmount);

      // Set initial values
      setClegCount(building?.repairCost || 0);
      setIronCount(building?.repairIronCost || 0);
      setStoneCount(building?.repairStoneCost || 0);
    }
  }, [building]);

  // Hash-based modal opening logic
  useEffect(() => {
    const tryOpenFromHash = (hash: string) => {
      if (!storeBuildings || !staticBuildings || !getBuilding || !hash || open)
        return;

      const hashToBuildingId: Record<string, number> = {
        "#upgrade-cleg": 3, // Cleg Mine
        "#upgrade-stone": 4, // Stone Mine
        "#upgrade-iron": 5, // Iron Mine
        "#upgrade-barracks": 6, // Barracks
      };

      const buildingId = hashToBuildingId[hash];
      if (!buildingId) return;

      const staticBuilding = staticBuildings.find((b) => b.id === buildingId);
      const apiBuilding = storeBuildings.find((b) => b.buildingId === buildingId);

      if (!staticBuilding) return;

      if (apiBuilding) {
        getBuilding(apiBuilding.id, { ...staticBuilding, ...apiBuilding });
      } else {
        getBuilding(buildingId, staticBuilding);
      }
    };

    // Attempt on mount/updates (covers direct deep-links)
    tryOpenFromHash(location.hash);

    // Also handle pure hash changes (BrowserRouter doesn't emit popstate on hashchange)
    const onHashChange = () => tryOpenFromHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [storeBuildings, staticBuildings, getBuilding, location.hash, open]);

  // Enhanced onClose to clear hash
  const handleClose = () => {
    if (location.hash) {
      navigate(window.location.pathname);
    }
    onClose();
  };

  if (!building) {
    return null;
  }

  const isBarracks = building?.buildingId === 6;
  const isOfferActive =
    new Date("2023-10-01T00:00:00.024Z")?.getTime() < Date.now() &&
    building?.buildingId === 6 &&
    building?.bonusLevel > 0 &&
    new Date("2023-10-09T00:00:00.024Z")?.getTime() > Date.now();
  const promotionActive = RepairPromotionEnd > Date.now();
  const buildingName = building?.name || "";

  // Resource slider handlers - following Home.tsx logic
  const handleChangeCleg = (val: number | number[]) => {
    const clg = typeof val === "number" ? val : val[0];
    setClegCount(clg);
    const totalAmount =
      clg +
      stoneCount * (building?.stoneMarketPrice || 0) +
      ironCount * (building?.ironMarketPrice || 0);
    if (totalAmount > maxSupply) {
      let remaining = totalAmount - maxSupply;
      if (+roundFloat(stoneCount - remaining / 2) > 0) {
        setStoneCount(
          (prev) =>
            +roundFloat(
              prev - remaining / 2 / (building?.stoneMarketPrice || 1)
            )
        );
      } else {
        setStoneCount(0);
        if (ironCount - remaining / (building?.ironMarketPrice || 1) < 0)
          return;
        setIronCount(
          (prev) =>
            +roundFloat(prev - remaining / (building?.ironMarketPrice || 1))
        );
        return;
      }
      if (+roundFloat(ironCount - remaining / 2) > 0)
        setIronCount(
          (prev) =>
            +roundFloat(prev - remaining / 2 / (building?.ironMarketPrice || 1))
        );
      else {
        setIronCount(0);
      }
    }
  };

  const handleChangeStone = (val: number | number[]) => {
    const stone = typeof val === "number" ? val : val[0];
    setStoneCount(stone);
    const totalAmount =
      clegCount +
      stone * (building?.stoneMarketPrice || 0) +
      ironCount * (building?.ironMarketPrice || 0);
    if (totalAmount > maxSupply) {
      const remaining = totalAmount - maxSupply;
      if (+roundFloat(clegCount - remaining / 2) > 0)
        setClegCount((prev) => +roundFloat(prev - remaining / 2));
      if (+roundFloat(ironCount - remaining / 2) > 0)
        setIronCount(
          (prev) =>
            +roundFloat(prev - remaining / 2 / (building?.ironMarketPrice || 1))
        );
    }
  };

  const handleChangeIron = (val: number | number[]) => {
    const iron = typeof val === "number" ? val : val[0];
    setIronCount(iron);
    const totalAmount =
      clegCount +
      stoneCount * (building?.stoneMarketPrice || 0) +
      iron * (building?.ironMarketPrice || 0);
    if (totalAmount > maxSupply) {
      const remaining = totalAmount - maxSupply;
      if (+roundFloat(clegCount - remaining / 2) > 0)
        setClegCount((prev) => +roundFloat(prev - remaining / 2));
      if (+roundFloat(stoneCount - remaining / 2) > 0)
        setStoneCount(
          (prev) =>
            +roundFloat(
              prev - remaining / 2 / (building?.stoneMarketPrice || 1)
            )
        );
    }
  };

  // HP the sliders currently buy back. maxSupply is the cost of a full repair,
  // so spending a fraction of it restores that fraction of the missing health.
  const calculateHpDelta = () => {
    if (!maxSupply) return 0;

    try {
      const spend = new Decimal(clegCount || 0)
        .plus(stoneCount * (building?.stoneMarketPrice || 0))
        .plus(ironCount * (building?.ironMarketPrice || 0));

      return new Decimal(100)
        .minus(building?.health || 0)
        .dividedBy(maxSupply)
        .times(spend)
        ?.toDecimalPlaces(2)
        .toNumber();
    } catch (e) {
      return 0;
    }
  };

  // Calculate remaining health after repair
  const handleCalculateRemaining = () => {
    const hpDelta = Math.max(0, calculateHpDelta());
    return roundFloat(Math.min(100, (building?.health || 0) + hpDelta));
  };

  // Discounted resource amount for the bulk-repair tier the sliders qualify for.
  // returnRaw gives the undiscounted amount — that is what the repair request bills.
  const handleCalculateHP = (resourceCount: number, returnRaw?: boolean) => {
    if (!resourceCount) return 0;

    const hpDelta = calculateHpDelta();
    if (hpDelta < 0) return 0;
    if (returnRaw) return resourceCount;

    if (hpDelta >= 70) {
      return Number((resourceCount - (resourceCount * 50) / 100)?.toFixed(2));
    }

    if (hpDelta >= 50) {
      return Number((resourceCount - (resourceCount * 30) / 100)?.toFixed(2));
    }

    if (hpDelta >= 20) {
      return Number((resourceCount - (resourceCount * 10) / 100)?.toFixed(2));
    }

    // If no discount applies
    return 0;
  };

  const handleUpgrade = () => {
    setConfirmDialog({
      open: true,
      submit: async () => {
        try {
          await onUpgrade(building?.id);
          // Close dialogs and refresh data on success
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          handleClose();
          refreshData();
          refreshUserInfo();
        } catch (error) {
          // Error handling is done in the parent component
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
      msg: isBarracks
        ? t("village:dialogs.upgradeConfirm.upgradeOnly", {
            name: buildingName,
            level: parseInt(building?.level || "0") + 1,
          })
        : t("village:dialogs.upgradeConfirm.repairAndUpgrade", {
            name: buildingName,
            level: parseInt(building?.level || "0") + 1,
          }),
      title: t("village:dialogs.confirm.title"),
    });
  };

  const handleCollect = () => {
    setConfirmDialog({
      open: true,
      submit: async () => {
        try {
          await onCollect(building?.id);
          // Close dialogs and refresh data on success
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          handleClose();
          refreshData();
          refreshUserInfo();
        } catch (error) {
          // Error handling is done in the parent component
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
      msg: t("village:dialogs.collectConfirm.message", {
        name: buildingName,
      }),
      title: t("village:dialogs.confirm.title"),
    });
  };

  const handleBuild = () => {
    setConfirmDialog({
      open: true,
      submit: async () => {
        try {
          await onBuild(building?.buildingId, building?.name, building);
          // Close dialogs and refresh data on success
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          handleClose();
          refreshData();
          refreshUserInfo();
        } catch (error) {
          // Error handling is done in the parent component
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
      msg: (
        <>
          {t("village:dialogs.buildConfirm.message", {
            name: buildingName,
          })}
          <BuildCostPreview building={building} />
        </>
      ),
      title: t("village:dialogs.confirm.title"),
    });
  };

  // Calculate the discount rate for repair offers
  const getOff = () => {
    const hpDelta = calculateHpDelta();

    if (hpDelta >= 70) {
      return t("village:discounts.offPercent", { percent: 50 });
    }
    if (hpDelta >= 50) {
      return t("village:discounts.offPercent", { percent: 30 });
    }
    if (hpDelta >= 20) {
      return t("village:discounts.offPercent", { percent: 10 });
    }
    return false;
  };

  return (
    <>
      <Dialog
        layoutLoading={detailLoading}
        noFooter
        size="lg"
        title={building?.name}
        open={open}
        onClose={handleClose}
      >
        <div className="row">
          {isOfferActive && (
            <div className="col-12 mb-2">
              <img
                style={{ borderRadius: "10px", marginTop: "-15px" }}
                width={"100%"}
                src={`/assets/images/barracks-offers-v2/${building?.bonusLevel}.png`}
              />
            </div>
          )}

          {/* Building image column */}
          <div className="col-12 col-lg-4 d-none d-sm-block ">
            <div className="col-12 col-sm-8 offset-sm-2 col-lg-12 offset-lg-0">
              <div className="d-flex justify-content-center">
                <div className="main-box-item text-light py-5 ">
                  <div className="mb-title text-center pt-3">
                    <h3 className="h6 fw-bold fs-med">
                      {t("common:labels.level")}
                      <button
                        className="mb-title-btn fw-bold text-light mx-2"
                        name="lblLevel"
                      >
                        {building?.level || 0}
                      </button>
                    </h3>
                  </div>
                  <div className="mb-body px-4 my-2">
                    <div className="image px-2 text-center">
                      <img
                        src={
                          FTPURL +
                          "/" +
                          (building?.photoFile || building?.photo)
                        }
                        className="img-fluid my-2"
                        alt={building?.name}
                      />
                    </div>
                  </div>
                  <div className="mb-button text-center">
                    {building?.status === PlayerBuildingState.NotHave ? (
                      <Button
                        badgeStyles={{ right: 0, top: 3 }}
                        badge={
                          building?.discount > 0 ? (
                            <>
                              <div className="dark-yellow fs-lg">
                                {building.discount}%
                              </div>
                              <small className="dark-yellow">
                                {t("village:labels.off")}
                              </small>
                            </>
                          ) : (
                            ""
                          )
                        }
                        onClick={handleBuild}
                        variant="rounded"
                        className="form-btn lightbrown-btn mb-4 mx-auto"
                      >
                        <span className="b-text fs-6 px-5">
                          {t("village:actions.build")}
                        </span>
                      </Button>
                    ) : isBarracks ? (
                      <Button
                        onClick={() => {
                          handleClose();
                          navigate("/barracks");
                        }}
                        variant="rounded"
                        className="form-btn lightbrown-btn mb-4 mx-auto"
                      >
                        <span className="b-text fs-6 px-5">
                          {t("common:actions.open")}
                        </span>
                      </Button>
                    ) : building?.status === PlayerBuildingState.Ready &&
                      !isBarracks ? (
                      <div className="text-center">
                        <Button
                          onClick={() => {
                            handleClose();
                            navigate("/marketplace?id=2");
                          }}
                          variant="rounded"
                          className="form-btn lightbrown-btn mb-4 mx-auto"
                        >
                          <span className="b-text fs-6 px-5">
                            {t("village:actions.buyLand")}
                          </span>
                        </Button>
                      </div>
                    ) : (
                      <div className="snow-layer">
                        <Button
                          onClick={handleCollect}
                          variant="rounded"
                          className="form-btn lightbrown-btn mb-4 mx-auto"
                        >
                          <span className="b-text fs-6 px-5">
                            {t("village:actions.collect")}
                          </span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Building details column */}
          <div className="col-12 col-lg-8">
            <div className="row mb-0 mb-sm-2">
              <div style={{ flex: 1 }} className="col-12 col-sm-8 col-lg-9">
                <div className="form-processbar fs-med">
                  <div className="mt-3">
                    <label className="d-flex justify-content-between mb-1">
                      <span>
                        {isBarracks
                          ? t("village:sections.queue")
                          : t("village:sections.health")}
                      </span>
                      <span>
                        {isBarracks
                          ? `${building?.trainingQueue} / ${building?.maxTrainingQueue}`
                          : `${roundFloat(building?.health)} / 100`}
                        {!isBarracks && activeTab === 1 && (
                          <>
                            <img
                              width={20}
                              height={20}
                              className={"mx-2"}
                              src={"/assets/images/icons/arrow-right.svg"}
                            />
                            <span>{handleCalculateRemaining()}</span>
                          </>
                        )}
                      </span>
                    </label>
                    <div className="progress wide">
                      <div
                        className={`progress-bar progress-bar-striped ${
                          building?.health >= 80
                            ? "green"
                            : building?.health >= 60
                            ? "blue"
                            : building?.health >= 40
                            ? "orange"
                            : building?.health >= 20
                            ? "darkOrange"
                            : building?.health < 20
                            ? "red"
                            : "green"
                        }`}
                        style={{
                          width:
                            (isBarracks
                              ? (
                                  (parseInt(String(building?.trainingQueue)) /
                                    building?.maxTrainingQueue) *
                                  100
                                ).toString()
                              : building?.health) + "%",
                        }}
                      ></div>
                    </div>
                    {building?.repairCost > 0 && (
                      <div className="d-flex flex-wrap mt-2 fs-small align-items-center">
                        <div>{t("village:sections.repairCost")}:</div>
                        {building?.repairCost && (
                          <div className="d-flex ms-2 align-items-center">
                            <img
                              src={"/assets/images/icons/cleg.png"}
                              className="me-1"
                              width={20}
                              height={20}
                            />
                            {roundFloat(building?.repairCost, 2)}
                          </div>
                        )}
                        {building?.repairStoneCost ? (
                          <div className="d-flex ms-2 align-items-center">
                            <img
                              src={"/assets/images/icons/stone.png"}
                              className="me-1"
                              width={20}
                              height={20}
                            />
                            {roundFloat(building?.repairStoneCost, 2)}
                          </div>
                        ) : (
                          ""
                        )}
                        {building?.repairIronCost ? (
                          <div className="d-flex ms-2 align-items-center">
                            <img
                              src={"/assets/images/icons/metal.png"}
                              className="me-1"
                              width={20}
                              height={20}
                            />
                            {roundFloat(building?.repairIronCost, 2)}
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {!isBarracks &&
                building?.status !== PlayerBuildingState.NotHave && (
                  <div className="col-12 col-sm-4 col-lg-3 pt-3 mb-1 mb-md-2 text-center text-md-right">
                    <Button
                      disabled={
                        detailLoading ||
                        building?.status === 0 ||
                        activeTab === 1
                      }
                      onClick={() =>
                        setConfirmDialog({
                          open: true,
                          submit: async () => {
                            const repaired = await onRepair(building?.id);
                            setConfirmDialog((prev) => ({
                              ...prev,
                              open: false,
                            }));
                            // The failure toast is already shown; leave the
                            // detail dialog open so the repair can be retried.
                            if (!repaired) return;
                            // onRepair already refreshed the buildings —
                            // this only clears the #hash and the user bar.
                            handleClose();
                            refreshUserInfo();
                          },
                          msg: t("village:dialogs.repairConfirm.message", {
                            name: buildingName,
                          }),
                          title: t("village:dialogs.confirm.title"),
                        })
                      }
                      variant="default"
                    >
                      {t("village:actions.repair")}
                    </Button>
                  </div>
                )}
            </div>

            {/* Tabs */}
            <div className="row mb-2 mb-md-4">
              <div className="col-12 col-sm-7 col-md-7 mb-3 mb-md-0 order-1 order-sm-0">
                <BuildingDetailTab
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isBuildingBarracks={isBarracks}
                />
              </div>
              <div className="col-12 col-sm-5 col-md-5 d-flex justify-content-center align-items-center mb-3 mb-md-0 order-0 order-sm-1">
                <img className="img-fluid" src="/assets/images/brown-sep.png" />
              </div>
            </div>

            {/* Tab Content */}
            <div className="row">
              <div className="col-12 position-relative">
                {activeTab === 0 && (
                  <div className="tab-box-body p-3 p-lg-4 pb-lg-2">
                    <div className="land-info-list text-light fs-small mb-2">
                      <div className="land-info-item d-flex justify-content-between">
                        <div className="title fw-normal">
                          {isBarracks
                            ? t("village:sections.troopsCapacity")
                            : t("village:sections.capacity")}
                        </div>
                        <div className="value fw-bold">
                          {roundFloat(building?.capacity)}
                          {building?.level !== 8 &&
                            building?.nextLevelCapacity && (
                              <span className="color-blue ms-1">
                                +
                                {roundFloat(
                                  building?.nextLevelCapacity -
                                    building?.capacity
                                )}
                              </span>
                            )}
                        </div>
                      </div>
                      <div className="land-info-item d-flex justify-content-between">
                        <div className="title fw-normal">
                          {isBarracks
                            ? t("village:sections.dailyHealing")
                            : t("village:sections.dailyProduction")}
                        </div>
                        <div className="value fw-bold">
                          {roundFloat(building?.currentDailyProduction)}
                          {building?.level !== 8 &&
                            building?.nextLevelDailyProduction && (
                              <span className="color-blue ms-1">
                                +
                                {roundFloat(
                                  building?.nextLevelDailyProduction -
                                    building?.currentDailyProduction
                                )}
                              </span>
                            )}
                        </div>
                      </div>
                      {isBarracks && (
                        <>
                          <div className="land-info-item d-flex justify-content-between">
                            <div className="title fw-normal">
                              <span>{t("village:sections.bonusStats")}</span>
                            </div>
                            <div className="value fw-bold">
                              {building?.bonusStats || 0}%
                              {building?.level !== 8 &&
                                building?.nextLevelBonusStats && (
                                  <span className="color-blue ms-1">
                                    +
                                    {building?.nextLevelBonusStats -
                                      (building?.bonusStats || 0)}
                                    %
                                  </span>
                                )}
                            </div>
                          </div>
                          <div className="land-info-item d-flex justify-content-between">
                            <div className="title fw-normal">
                              <span>{t("village:sections.trainingQueue")}</span>
                            </div>
                            <div className="value fw-bold">
                              {building?.maxTrainingQueue}
                              {building?.level !== 8 &&
                                building?.nextLevelMaxTrainingQueue && (
                                  <span className="color-blue ms-1">
                                    +
                                    {building?.nextLevelMaxTrainingQueue -
                                      building?.maxTrainingQueue}
                                  </span>
                                )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {building?.level !== 8 &&
                      building?.status !== PlayerBuildingState.NotHave && (
                        <div className="text-center">
                          <div className="d-flex flex-column align-items-center">
                            {building?.upgradeCost && (
                              <div
                                style={{ marginBottom: 8 }}
                                className="d-flex mt-3 gap-2 flex-wrap align-items-end justify-content-center"
                              >
                                <div className="">
                                  {t("common:labels.cost")}:
                                </div>
                                <div className="d-flex ms-2 gap-2 flex-wrap align-items-end">
                                  {building?.lastUpgradeCost &&
                                  building?.lastUpgradeCost !==
                                    building?.upgradeCost ? (
                                    <div>
                                      <s>
                                        {roundFloat(
                                          building?.lastUpgradeCost +
                                            (building?.repairCost || 0),
                                          3
                                        )}
                                      </s>
                                      <div className="text-success-light">
                                        <img
                                          src={"/assets/images/icons/cleg.png"}
                                          width={22}
                                          height={22}
                                        />
                                        &nbsp;
                                        {roundFloat(
                                          building?.upgradeCost +
                                            (building?.repairCost || 0),
                                          2
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                               
                                      <img
                                        src={"/assets/images/icons/cleg.png"}
                                        width={22}
                                        height={22}
                                      />
                                      {roundFloat(
                                        +building?.upgradeCost +
                                          Number(building?.repairCost),
                                        3
                                      )}
                                    </>
                                  )}

                                  {Boolean(building?.upgradeStoneCost) &&
                                    (building?.lastUpgradeStoneCost &&
                                    building?.lastUpgradeStoneCost !==
                                      building?.upgradeStoneCost ? (
                                      <div>
                                        <s>
                                          {roundFloat(
                                            building?.lastUpgradeStoneCost +
                                              (building?.repairStoneCost || 0),
                                            3
                                          )}
                                        </s>
                                        <div className="text-success-light">
                                          <img
                                            src={
                                              "/assets/images/icons/stone.png"
                                            }
                                            width={22}
                                            height={22}
                                          />
                                          &nbsp;
                                          {roundFloat(
                                            building?.upgradeStoneCost +
                                              (building?.repairStoneCost || 0),
                                            2
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <img
                                          src={"/assets/images/icons/stone.png"}
                                          width={22}
                                          height={22}
                                        />
                                        {roundFloat(
                                          building?.upgradeStoneCost +
                                            (building?.repairStoneCost || 0),
                                          3
                                        )}
                                      </>
                                    ))}

                                  {Boolean(building?.upgradeIronCost) &&
                                    (building?.lastUpgradeIronCost &&
                                    building?.lastUpgradeIronCost !==
                                      building?.upgradeIronCost ? (
                                      <div>
                                        <s>
                                          {roundFloat(
                                            building?.lastUpgradeIronCost +
                                              (building?.repairIronCost || 0),
                                            3
                                          )}
                                        </s>
                                        <div className="text-success-light">
                                          <img
                                            src={
                                              "/assets/images/icons/metal.png"
                                            }
                                            width={22}
                                            height={22}
                                          />
                                          &nbsp;
                                          {roundFloat(
                                            building?.upgradeIronCost +
                                              (building?.repairIronCost || 0),
                                            2
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <img
                                          src={"/assets/images/icons/metal.png"}
                                          width={22}
                                          height={22}
                                        />
                                        {roundFloat(
                                          building?.upgradeIronCost +
                                            (building?.repairIronCost || 0),
                                          3
                                        )}
                                      </>
                                    ))}
                                </div>
                              </div>
                            )}
                            <Button
                              badge={
                                building?.discount > 0 ? (
                                  <>
                                    <div className="dark-yellow fs-lg">
                                      {building.discount}%
                                    </div>
                                    <small className="dark-yellow">
                                      {t("village:labels.off")}
                                    </small>
                                  </>
                                ) : null
                              }
                              onClick={handleUpgrade}
                              variant="wide-primary"
                              className="mt-3"
                            >
                              {isBarracks ? (
                                <span>{t("village:actions.upgradeBarracks")}</span>
                              ) : (
                                <span>{t("village:actions.repairAndUpgrade")}</span>
                              )}
                              {building?.upgradeEmberReward > 0 ? (
                                <div className="badge-fire">
                                  <div>{t("village:actions.earn")}</div>+
                                  {ThousandsSeparator(
                                    building?.upgradeEmberReward
                                  )}
                                  <img
                                    src={Assets.icons.ember}
                                    width={18}
                                    height={18}
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {activeTab === 1 &&
                  !isBarracks &&
                  building?.status !== PlayerBuildingState.NotHave && (
                    <div className="tab-box-body sec p-3 p-lg-4">
                      <div className="d-flex align-items-center flex-wrap">
                        <span style={{ minWidth: 50 }}>
                          {t("village:resources.cleg")}
                        </span>
                        <Slider
                          className="mx-3"
                          style={{ flex: 1, minWidth: 100 }}
                          value={clegCount}
                          onChange={(val) => handleChangeCleg(val)}
                          min={0}
                          max={maxSupply}
                        />
                        <div
                          style={{ minWidth: 170 }}
                          className="fs-med text-end text-md-start dark-yellow ms-auto mt-1 mt-md-0 "
                        >
                          <img
                            src={"/assets/images/icons/cleg.png"}
                            className="me-2"
                            width={20}
                            height={20}
                          />
                          <span className="">
                            {ThousandsSeparator(roundFloat(clegCount))}
                          </span>
                        </div>
                      </div>
                      {building?.stoneMarketPrice && (
                        <div className="d-flex mt-2 align-items-center flex-wrap">
                          <span style={{ minWidth: 50 }}>
                            {t("village:resources.stone")}
                          </span>
                          <Slider
                            className="mx-3"
                            style={{ flex: 1, minWidth: 100 }}
                            value={stoneCount}
                            onChange={(val) => handleChangeStone(val)}
                            min={0}
                            max={
                              +roundFloat(
                                maxSupply / building?.stoneMarketPrice
                              )
                            }
                          />
                          <div
                            style={{ minWidth: 170 }}
                            className="fs-med text-end text-md-start ms-auto mt-1 mt-md-0 dark-yellow"
                          >
                            <img
                              src={"/assets/images/icons/stone.png"}
                              width={20}
                              height={20}
                              className="me-1"
                            />
                            <span className="p">
                              {ThousandsSeparator(roundFloat(stoneCount))}
                            </span>
                            <span className="fs-med">
                              &nbsp;(1{" "}
                              <img
                                src={"/assets/images/icons/stone.png"}
                                width={16}
                                height={16}
                                className="me-1"
                              />
                              ={building?.stoneMarketPrice}
                              <img
                                src={"/assets/images/icons/cleg.png"}
                                width={20}
                                height={20}
                                className="ms-1"
                              />
                              )
                            </span>
                          </div>
                        </div>
                      )}
                      {building?.ironMarketPrice && (
                        <div className="d-flex mt-2 align-items-center flex-wrap">
                          <span style={{ minWidth: 50 }}>
                            {t("village:resources.iron")}
                          </span>
                          <Slider
                            className="mx-3"
                            style={{ flex: 1, minWidth: 100 }}
                            value={ironCount}
                            onChange={(val) => handleChangeIron(val)}
                            min={0}
                            max={
                              +roundFloat(maxSupply / building?.ironMarketPrice)
                            }
                          />
                          <div
                            style={{ minWidth: 170 }}
                            className="fs-med ms-auto text-end text-md-start mt-1 mt-md-0 dark-yellow"
                          >
                            <img
                              src={"/assets/images/icons/metal.png"}
                              width={20}
                              height={20}
                              className="me-1"
                            />
                            <span className="p">
                              {ThousandsSeparator(roundFloat(ironCount))}
                            </span>
                            <span className="fs-med">
                              &nbsp;(1{" "}
                              <img
                                src={"/assets/images/icons/metal.png"}
                                width={16}
                                height={16}
                                className="me-1"
                              />
                              ={building?.ironMarketPrice}
                              <img
                                src={"/assets/images/icons/cleg.png"}
                                width={20}
                                height={20}
                                className="ms-1"
                              />
                              )
                            </span>
                          </div>
                        </div>
                      )}
                      <br />
                      {promotionActive &&
                      building?.health < 99 &&
                      (handleCalculateHP(clegCount) ||
                        handleCalculateHP(stoneCount) ||
                        handleCalculateHP(ironCount)) ? (
                        <motion.div
                          transition={{ duration: 0.5 }}
                          initial={isMobile ? undefined : { opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ x: -1600 }}
                          className="mt-3"
                        >
                          <Alert variant="success" className="pt-3">
                            <Badge
                              style={{
                                position: "absolute",
                                top: "-11px",
                                right: 0,
                                left: 0,
                                marginInline: "auto",
                                width: "fit-content",
                              }}
                              bg="danger"
                            >
                              {t("village:specialOffer.title")}{" "}
                              <small style={{ fontWeight: 600 }}>
                                (
                                <Countdown
                                  zeroPadTime={0}
                                  date={RepairPromotionEnd}
                                  renderer={({ formatted, days }) => (
                                    <>
                                      {days > 0 ? `${days}d ` : ""}
                                      {formatted.hours}:{formatted.minutes}:
                                      {formatted.seconds}{" "}
                                      {t("village:specialOffer.left")}
                                    </>
                                  )}
                                />
                                )
                              </small>
                            </Badge>
                            <div className="row text-center justify-content-center">
                              {handleCalculateHP(clegCount) > 0 ? (
                                <div className="col-6 col-md-4 ">
                                  <div className="fs-tiny">
                                    <span className="text-decoration-line-through text-secondary">
                                      {clegCount}
                                    </span>
                                    &nbsp;
                                    <b className="text-success">{getOff()}</b>
                                  </div>
                                  <div className="bold">
                                    <img
                                      src={"/assets/images/icons/cleg.png"}
                                      width={20}
                                      height={20}
                                      className="me-1"
                                    />
                                    {handleCalculateHP(clegCount)}
                                  </div>
                                </div>
                              ) : (
                                ""
                              )}
                              {handleCalculateHP(stoneCount) > 0 ? (
                                <div className="col-6 col-md-4">
                                  <div className="fs-tiny">
                                    <span className="text-decoration-line-through text-secondary">
                                      {stoneCount}
                                    </span>
                                    &nbsp;
                                    <b className="text-success">{getOff()}</b>
                                  </div>
                                  <div className="bold">
                                    <img
                                      src={"/assets/images/icons/stone.png"}
                                      width={20}
                                      height={20}
                                      className="me-1"
                                    />
                                    {handleCalculateHP(stoneCount)}
                                  </div>
                                </div>
                              ) : (
                                ""
                              )}
                              {handleCalculateHP(ironCount) > 0 ? (
                                <div className="col-6 col-md-4">
                                  <div className="fs-tiny">
                                    <span className="text-decoration-line-through text-secondary">
                                      {ironCount}
                                    </span>
                                    &nbsp;
                                    <b className="text-success">{getOff()}</b>
                                  </div>
                                  <div className="bold">
                                    <img
                                      src={"/assets/images/icons/metal.png"}
                                      width={20}
                                      height={20}
                                      className="me-1"
                                    />
                                    {handleCalculateHP(ironCount)}
                                  </div>
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          </Alert>
                        </motion.div>
                      ) : (
                        ""
                      )}
                      <div className="mt-3 text-center w-100">
                        <Button
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              submit: async () => {
                                const repairOptions = {
                                  clegUse: roundFloat(
                                    handleCalculateHP(clegCount, true),
                                    4,
                                    Decimal.ROUND_DOWN
                                  ),
                                  ironUse: roundFloat(
                                    handleCalculateHP(ironCount, true),
                                    4,
                                    Decimal.ROUND_DOWN
                                  ),
                                  stoneUse: roundFloat(
                                    handleCalculateHP(stoneCount, true),
                                    4,
                                    Decimal.ROUND_DOWN
                                  ),
                                };
                                const repaired = await onRepair(
                                  building?.id,
                                  repairOptions
                                );
                                setConfirmDialog((prev) => ({
                                  ...prev,
                                  open: false,
                                }));
                                // The failure toast is already shown; leave the
                                // detail dialog open so the repair can be retried.
                                if (!repaired) return;
                                // onRepair already refreshed the buildings —
                                // this only clears the #hash and the user bar.
                                handleClose();
                                refreshUserInfo();
                              },
                              msg: t("village:dialogs.repairResourcesConfirm.message", {
                                name: buildingName,
                              }),
                              title: t("village:dialogs.confirm.title"),
                            })
                          }
                          className="mx-auto"
                          variant="wide-primary"
                        >
                          {t("village:actions.repairBuilding", {
                            name: buildingName,
                          })}
                        </Button>
                      </div>
                    </div>
                  )}

                {activeTab === 1 && isBarracks && (
                  <div className="tab-box-body sec p-3 p-lg-4">
                    <div className="mt-3 text-center">
                      <h4>{t("village:sections.barracksInformationTitle")}</h4>
                      <p>
                        {t("village:sections.barracksInformationBody")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        loading={loading}
        title={confirmDialog?.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={confirmDialog?.submit}
        open={confirmDialog?.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        {confirmDialog?.msg}
      </Dialog>
    </>
  );
};

export default BuildingDetailDialog;
